import React, { useContext, useEffect, useMemo, Suspense, lazy } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";
import { AuthContext } from "./AuthContext";
import IntercomConfig from "../Integration/Intercom/IntercomConfig";
import ClarityConfig from "../Integration/Clarity/ClarityConfig";
import LazyLoadErrorBoundary from "./LazyLoadErrorBoundary";
import FocusOnlyGuard from "./FocusOnlyGuard";
import { GetSubscriptionStatus } from "../Api/Billing";

// PERFORMANCE OPTIMIZATION: Lazy load all route components
// This reduces initial bundle size from ~3MB to ~400KB
// Each route component loads only when navigated to

// Onboarding flows
const OnboardingLanding = lazy(() => import("../Components/OnboardingLanding/OnboardingLanding"));
const OnboardingGetStarted = lazy(() => import("../Pages/OnboardingGetStarted/OnboardingGetStarted"));
const OnBoarding = lazy(() => import("../Pages/OnBoardinngNotFinish/OnBoardinngNotFinish"));
const OnboardingCompleted = lazy(() => import("../Pages/Onboarding/Onboarding"));
const OnboardingRedirect = lazy(() => import("../Components/OnboardingRedirect/OnboardingRedirect"));
const OnboardBusinessDetails = lazy(() => import("../Pages/OnboardBusinessDetails/OnboardBusinessDetails"));
const OnboardingBrand = lazy(() => import("../Pages/OnboardingBrand/OnboardingBrand"));

// Admin and User Management
const AdminUsersViews = lazy(() => import("../Pages/AdminUsersViews/AdminUsersViews"));
const EmployeeTrainingApp = lazy(() => import("../Pages/Review/TrainingReviewPage")); // /training-app — same review page + modals
const AdminSecondaryTabs = lazy(() => import("../Components/AdminSecondaryTabs/AdminSecondaryTabs"));
const GroupTypeList = lazy(() => import("../Components/Groups/GroupTypelist"));

// Organization and Profile
const PublicView = lazy(() => import("../Components/OrganizationOverView/PublicView"));
const MyProfile = lazy(() => import("../Components/MyProfile/MyProfileView"));
const ManageProfile = lazy(() => import("../Components/MyProfile/ManageProfile"));

// Policy Management (Large: 6,661 lines)
const PolicyReview = lazy(() => import("../Components/PolicyReview/PolicyReview"));
const PolicyWizardExecution = lazy(() => import("../Pages/PolicyWizardExecution/PolicyWizardExecution"));

// Focus Mode (Main landing page - keep lightweight)
const FocusMode = lazy(() => import("../Pages/FocusMode/FocusMode"));
const FocusModeV2 = lazy(() => import("../Pages/FocusMode/FocusModeV2"));
const FocusFrame = lazy(() => import("../Pages/FocusMode/FocusFrame"));
const FocusHome = lazy(() => import("../Pages/FocusMode/FocusHome"));
const FocusExpiredPage = lazy(() => import("../Pages/FocusMode/FocusExpiredPage"));
const FocusModeControlList = lazy(() => import("../Pages/FocusMode/FocusModeControlList"));
const FocusModeControlCenter = lazy(() => import("../Pages/FocusMode/FocusModeControlCenter"));
const FocusModeAuditNotesList = lazy(() => import("../Pages/FocusMode/FocusModeAuditNotesList"));

// Audit Module
const AuditControlReviewPage = lazy(() => import("../Pages/Audit/AuditControlReviewPage"));

// Utilities
const TwoFactorModule = lazy(() => import("../Components/TwoFactorModule/TwoFactorModule"));
const DocumentViewer = lazy(() => import("../Components/DocumentViewer/DocumentViewer"));
const PublicTrustCenterPage = lazy(() => import("../Pages/PublicTrustCenter/PublicTrustCenterPage"));
const PublicAssignmentPage = lazy(() => import("../Pages/PublicAssignment/PublicAssignmentPage"));

// Loading fallback component
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column',
      gap: 2,
    }}
  >
    <CircularProgress size={50} />
    <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>Loading...</p>
  </Box>
);

const PrivateRoutes = () => {
  const { isAuthenticated } = useContext(AuthContext);
  // Parsed once per stored value: a fresh object on every render re-booted Intercom each time (BUG-25).
  const userDataRaw = localStorage.getItem('userLogindata');
  const userData = useMemo(() => { try { return JSON.parse(userDataRaw); } catch { return null; } }, [userDataRaw]);
  const navigate = useNavigate();
  const location = useLocation();

  // Route Guard: Block all URLs for expired users except /focus-expired
  useEffect(() => {
    if (isAuthenticated && userData) {
      const subscription_status = localStorage.getItem("subscription_status");
      const isExpired = subscription_status === "expired";
      const isOnExpiredPage = location.pathname === "/focus-expired";

      // Returning from Stripe Checkout (?checkout=success): live-sync the
      // subscription status BEFORE the expired-bounce. GetSubscriptionStatus
      // re-checks Stripe server-side and updates is_paid_client on the org,
      // so a just-paid expired org is reactivated without waiting for the
      // webhook or a re-login.
      const params = new URLSearchParams(location.search);
      if (isExpired && params.get('checkout') === 'success') {
        const orgId = localStorage.getItem('organizationId');
        if (orgId) {
          GetSubscriptionStatus(orgId)
            .then((res) => {
              const data = res?.data?.data ?? res?.data ?? {};
              if (data.is_paid_client) {
                localStorage.setItem('subscription_status', 'active');
                console.log('Checkout success - subscription reactivated, entering app');
                navigate('/view/focus', { replace: true });
              }
            })
            .catch(() => { /* stay put; user can retry from the expired page */ });
          return; // skip the expired-bounce while the sync is in flight
        }
      }

      // If expired and trying to access any route other than /focus-expired, redirect
      if (isExpired && !isOnExpiredPage) {
        console.log('Subscription expired - redirecting to /focus-expired from:', location.pathname);
        navigate('/focus-expired', { replace: true });
      }

      // If NOT expired but on /focus-expired page, redirect to dashboard
      if (!isExpired && isOnExpiredPage) {
        console.log('Subscription active - redirecting from /focus-expired to /view/focus');
        navigate('/view/focus', { replace: true });
      }
    }
  }, [isAuthenticated, userData, location.pathname, location.search, navigate]);

  return (
    <>
      {/* Only load analytics when the user is authenticated */}
      {isAuthenticated && userData && (
        <>
          <IntercomConfig userLogindata={userData} />
          <ClarityConfig user={userData} isPublic={false} />
        </>
      )}
      
      <LazyLoadErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
        {/* Protected routes - require authentication */}
        {isAuthenticated ? (
          <>
            <Route path="/dashboard" element={<OnboardingLanding />} />
            <Route exact path="/onboarding" element={<OnboardingGetStarted />} />
            <Route exact path="/bizdetails" element={<OnboardBusinessDetails />} />
            <Route exact path="/onboarding/brand" element={<OnboardingBrand />} />
            <Route path="/onboardingNotFinished" element={<OnBoarding />} />
            <Route path="/onboardingSuscessful" element={<OnboardingCompleted />} />
            <Route path="/onboarding/import/users" element={<OnboardingRedirect />} />
            <Route path="/adminlist" element={<FocusOnlyGuard><AdminUsersViews /></FocusOnlyGuard>} /> {/* Focus-only roles are sent back to /view/focus */}
            <Route path="/training-app" element={<EmployeeTrainingApp />} /> {/* Employee training pickup — same modals as Training Review */}
            <Route path="/adminusers" element={<AdminSecondaryTabs />} />
            <Route path="/adminusers/groupList" element={<GroupTypeList />} />
            <Route path="/organizationOverview" element={<PublicView />} />
            <Route path="/myProfile" element={<MyProfile />} />
            <Route path="/manageProfile" element={<ManageProfile />} />
            <Route path="/policyOverview" element={<PolicyReview />} />
            <Route path="/focus-expired" element={<FocusExpiredPage />} /> {/* Expired subscription page */}
            {/* Focus Mode: under Zavvy these pages open inside the chat frame (sidebar + rail); classic renders them alone */}
            <Route element={<FocusFrame />}>
              <Route path="/view/focus" element={<FocusHome />} /> {/* Zavvy chat or classic Focus Mode */}
              <Route path="/focus-projecttimeline" element={<FocusMode />} /> {/* Project timeline/onboarding flow */}
              <Route path="/focus-controllist" element={<FocusOnlyGuard><FocusModeControlList /></FocusOnlyGuard>} /> {/* Focus Mode control list grouped by Domain/Category/Tier */}
              <Route path="/focus-control-center" element={<FocusOnlyGuard><FocusModeControlCenter /></FocusOnlyGuard>} /> {/* Focus Mode full-screen control view */}
              <Route path="/focus-auditornotes" element={<FocusModeAuditNotesList />} /> {/* Focus Mode auditor notes (Organization + Internal Notes) */}
            </Route>
            <Route path="/view/focus-v3" element={<Navigate to="/view/focus" replace />} /> {/* Redirect to main focus route */}
            <Route path="/authentication" element={<TwoFactorModule />} />
            <Route path="/document-viewer" element={<DocumentViewer isPublic={false} />} />
            <Route path="/public/document-viewer" element={<DocumentViewer isPublic={true} />} />
            <Route path="/public/trust-center" element={<PublicTrustCenterPage />} />
            <Route path="/public/trust-center/:pillar" element={<PublicTrustCenterPage />} />
            <Route path="/public/assignment" element={<PublicAssignmentPage />} />
            
            {/* Policy Wizard Routes */}
            <Route path="/policy-wizard/execute/:wizardId" element={<PolicyWizardExecution />} />
            
            {/* Audit Module Routes */}
            <Route path="/audit/control-review/:fcoId" element={<AuditControlReviewPage />} />
            
            {/* User Review Routes - Navigate to AdminUsersViews with query params */}
            {/* Note: These routes are handled via the sidebar navigation in AdminUsersViews */}
            {/* The actual routing is done through /adminlist?view=<ViewName> */}
            
            {/* Catch-all route for authenticated users */}
            <Route path="*" element={<Navigate to="/view/focus" replace />} />
          </>
        ) : (
          // Redirect unauthenticated users to login page
          <Route path="*" element={<Navigate to="/" replace />} />
        )}
          </Routes>
        </Suspense>
      </LazyLoadErrorBoundary>
    </>
  );
};

export default PrivateRoutes;
